import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { isEmpty, isNil } from 'lodash'
import { IconBell, IconSettings } from '@tabler/icons-react'
import classNames from 'classnames'

import { useFlags } from 'launchdarkly-react-client-sdk'

import { resolveLiveCoaching } from '@/reducers/realtime/realtime.actions'
import { hasAgentAssistAccess, hasLiveListenAccess } from '@/reducers/auth/currentUser.redux'
import { TOGGLE_PANEL } from '@/reducers/notifications/notifications.redux'
import { Button } from '@/components/buttons/Button'

import {
  getNotificationActions,
  liveAlertForNotification,
  LIVE_COACHING_TYPES,
} from './notificationActions'
import { NOTIFICATION_ICONS } from './notificationTypes.constants'

// Don't cold-resolve rows older than any plausible live call — the resolver
// can only answer "not live" for them. Missing createdAt resolves (conservative).
const COLD_RESOLVE_MAX_AGE_MS = 6 * 60 * 60 * 1000

export const NotificationItem = ({ notification, onClickNotification }) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const currentUser = useSelector((state) => state.currentUser)
  const currentOrganization = useSelector((state) => state.currentOrganization)
  // raised_hand action buttons (Listen / Chat) need the agent's live-call
  // context — the active RTC alert, but only when it's for the SAME call this
  // notification is about (liveAlertForNotification), so an old raised-hand row
  // doesn't light up when a new call starts. Looked up by agent username when
  // refTable says refId IS an agent_username. Link-style types (coaching,
  // disputes, etc.) don't use this.
  const rtcAlert = useSelector((state) => {
    if (notification.refTable !== 'agent_username' || !notification.refId) {
      return null
    }
    return state.realtimeUserAlerts?.alerts?.[notification.refId] || null
  })
  // The agent currently being live-listened to (either listen surface) —
  // swaps the row's Listen action for a Stop when it's this row's agent.
  const listeningToUsername = useSelector(
    (state) =>
      state.realtimeUserAlerts?.listeningToUsername || state.commandCenter?.listeningToAgent || null
  )

  const isLiveCoaching = LIVE_COACHING_TYPES.includes(notification.notificationType)
  // Chat and Listen open different surfaces and are gated separately: chat is
  // manager-to-agent text, while Listen is live unredacted call audio and stays
  // behind the Live Listen flag.
  const flags = useFlags()
  const agentAssistAvailable = hasAgentAssistAccess(currentOrganization)
  const liveListenAvailable = hasLiveListenAccess(currentOrganization, flags)

  // Warm path: the manager was connected when the alert fired, so the agent's
  // live RTC alert is already in memory — matched to THIS call via the now-
  // persisted callId (falls back to metadata.call_id for in-flight rows).
  const warmContext = liveAlertForNotification(rtcAlert, notification, {
    requireCallId: isLiveCoaching,
  })

  // Cold path (Pass 2 mid-call join): no in-memory alert (the manager was
  // offline when it fired, or reloaded), but the row carries a persisted
  // call_id + agent (ref_id). Ask realtime-coaching, once, whether that call is
  // still live and for the current join context. Only for what's resolvable —
  // and only for rows young enough to plausibly still be live: rows re-resolve
  // on every panel open (components unmount on close), and call_started rows
  // in particular are never warm, so without the age gate a busy manager's
  // panel fires a resolver request per row per open, almost all for calls
  // that ended hours ago.
  const [resolved, setResolved] = useState(null)
  const resolveRequestedRef = useRef(false)
  const rowAgeMs = notification.createdAt
    ? Date.now() - new Date(notification.createdAt).getTime()
    : 0
  const canColdResolve =
    !warmContext &&
    isLiveCoaching &&
    notification.refTable === 'agent_username' &&
    Boolean(notification.refId) &&
    Boolean(notification.callId) &&
    rowAgeMs < COLD_RESOLVE_MAX_AGE_MS
  useEffect(() => {
    if (!canColdResolve || resolveRequestedRef.current) {
      return undefined
    }
    resolveRequestedRef.current = true
    // Guard the async setState: the toast reuse (QCER-989) can unmount this row
    // mid-resolve when it auto-dismisses at 20s, and a setResolved on an
    // unmounted component warns in React 16.
    let active = true
    dispatch(
      resolveLiveCoaching({ callId: notification.callId, agentUsername: notification.refId })
    ).then((result) => {
      if (active) {
        setResolved(result || null)
      }
    })
    return () => {
      active = false
    }
  }, [canColdResolve, notification.callId, notification.refId, dispatch])

  // Reshape the resolver result into the rtcAlert shape the Listen/Chat action
  // handlers already expect, so a cold join reuses the exact warm-path code.
  // Only when the call is still live — otherwise no join is offered.
  const coldContext =
    !warmContext && resolved?.isLive
      ? {
          callId: notification.callId,
          agentId: resolved.agentId,
          agentUsername: notification.refId,
          agentName:
            `${resolved.agentFirstName || ''} ${resolved.agentLastName || ''}`.trim() ||
            notification.refId,
          managerId: currentUser.user_id,
          managerUsername: currentUser.username,
          managerName: `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim(),
          processingUrl: resolved.processingUrl,
          // No persisted alert_id; the listen path resolves by call_id +
          // agent_username (matches the raised_hand sentinel).
          alertId: 'raised_hand',
        }
      : null

  const liveContext = warmContext || coldContext

  // Coverage nudge: when the manager can't coach this agent because their alert
  // configs don't cover them, show "Set up alerts". Coverage is the union of
  // all_users / selected-users (notifications.alertCoverage). The agent id is on
  // the WS push (metadata) for warm rows, and from the resolver for cold rows.
  // raised_hand-only: an RTA recipient covers the agent by definition, and warm
  // rows are covered by definition (the RTC socket only forwarded them because
  // the manager covers the agent) — so never nudge those.
  const alertCoverage = useSelector((state) => state.notifications?.alertCoverage)
  // This component also renders inside RTA toasts (NotificationToast), where
  // the panel is usually closed — TOGGLE_PANEL there would pop the panel OPEN.
  // The navigate-closes-panel rule only applies on the panel surface.
  const panelOpen = useSelector((state) => state.notifications?.panelOpen)
  const agentId = notification.metadata?.agent_id ?? resolved?.agentId
  // userIds comes from core's /api/alert_configs and agentId from the WS push /
  // resolver; compare as strings so an int-vs-string id mismatch between the
  // services can't silently read as "not covered".
  const coversThisAgent =
    alertCoverage && !isNil(agentId)
      ? alertCoverage.allUsers || alertCoverage.userIds.map(String).includes(String(agentId))
      : null
  const showSetupGuidance =
    notification.notificationType === 'raised_hand' &&
    !warmContext &&
    coversThisAgent === false &&
    !alertCoverage.hasTagConfigs
  const IconComponent = NOTIFICATION_ICONS[notification.notificationType] || IconBell
  const timeAgo = notification.createdAt
    ? formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })
    : ''

  const handleBodyClick = () => {
    onClickNotification(notification)
  }

  // While the cold-join resolve is still in flight we don't yet know if the call
  // is live, so live-vs-ended actions (RTA's "View call") hold off until it
  // settles rather than flashing a stale action (QCER-862 invariant).
  const coldResolvePending = canColdResolve && isNil(resolved)
  const actions = getNotificationActions(notification, liveContext, listeningToUsername, {
    resolvePending: coldResolvePending,
    agentAssistAvailable,
    liveListenAvailable,
  })

  // Mark read only once EVERY action on the row has been used (or the body is
  // clicked). With two live actions (Listen + Chat), pressing just one
  // shouldn't mark it read — the manager likely still wants the other, and a
  // read row drops out of the unread view. The panel's sticky-unread snapshot
  // keeps the row visible until close regardless.
  const [pressedActions, setPressedActions] = useState([])

  const handleActionClick = (action) => (event) => {
    // stopPropagation keeps the body's click handler from also firing (it
    // would mark the row read immediately, defeating the all-actions rule).
    event.stopPropagation()
    action.onClick({ notification, history, dispatch, rtcAlert: liveContext, currentUser })
    const pressed = pressedActions.includes(action.label)
      ? pressedActions
      : [...pressedActions, action.label]
    setPressedActions(pressed)
    if (actions.every((candidate) => pressed.includes(candidate.label))) {
      onClickNotification(notification)
    }
    // Navigating away is a "done with the panel" gesture — close it so it
    // doesn't sit on top of the destination page (QCER-901). Inline live-
    // coaching actions (Listen/Chat) stay: the manager keeps triaging.
    if (action.kind === 'link' && panelOpen) {
      dispatch({ type: TOGGLE_PANEL })
    }
  }

  const handleSetupAlertsClick = (event) => {
    event.stopPropagation()
    onClickNotification(notification)
    history.push('/realtime_coaching/alerts')
    // Same navigate-closes-panel rule as link-kind actions.
    if (panelOpen) {
      dispatch({ type: TOGGLE_PANEL })
    }
  }

  return (
    <div
      className={classNames('notification-item', {
        'notification-item--unread': !notification.read,
      })}
      data-testid={`notification-item-${notification.id}`}
    >
      <button type="button" className="notification-item__body" onClick={handleBodyClick}>
        <div className="notification-item__icon">
          <IconComponent size={18} />
        </div>
        <div className="notification-item__content">
          <div className="notification-item__title">{notification.title}</div>
          <div className="notification-item__message">{notification.message}</div>
          <div className="notification-item__time">{timeAgo}</div>
        </div>
        {!notification.read && <div className="notification-item__unread-dot" />}
      </button>

      {showSetupGuidance && (
        <div className="notification-item__actions">
          <Button
            xs
            className="notification-item__action"
            icon={<IconSettings />}
            onClick={handleSetupAlertsClick}
            data-testid={`notification-setup-alerts-${notification.id}`}
          >
            Set up alerts
          </Button>
        </div>
      )}

      {!showSetupGuidance && !isEmpty(actions) && (
        <div className="notification-item__actions">
          {actions.map((action) => {
            const ActionIcon = action.icon
            return (
              <Button
                key={action.label}
                xs
                className="notification-item__action"
                icon={ActionIcon ? <ActionIcon /> : null}
                onClick={handleActionClick(action)}
                data-testid={`notification-action-${notification.id}-${action.label
                  .toLowerCase()
                  .replace(/\s+/g, '-')}`}
              >
                {action.label}
              </Button>
            )
          })}
        </div>
      )}
    </div>
  )
}
